import { useEffect, useState } from 'react';

// Parse French locale numbers (virgule as decimal separator)
const parseLocaleNumber = (value) => {
    if (typeof value === 'number') return value;
    return Number(String(value).replace(',', '.'));
};

// Format number to French locale (virgule as decimal separator)
const formatLocaleNumber = (value, decimals = 2) => {
    return Number(value || 0).toFixed(decimals).replace('.', ',');
};

const getLastPriceForIngredient = (ingredient) => {
    const value = ingredient?.last_price ?? ingredient?.lastPrice ?? ingredient?.purchasePrice ?? ingredient?.cost_per_unit ?? ingredient?.purchase_price ?? ingredient?.price ?? ingredient?.unit_cost;
    return value == null || value === '' ? null : Number(value);
};

const getInitialFormState = (units) => ({
    id: null,
    name: '',
    marque: '',
    caracteristique: '',
    // store textual values for inputs (localized strings like "1,23")
    stock: '',
    threshold: '0',
    unit_id: units[0]?.id ? String(units[0].id) : '',
    purchasePrice: '',
    supplier: '',
    purchaseDate: '',
    perishable: false,
    expirationDate: '',
});

export default function IngredientsTab({ ingredients = [], units = [], suppliers = [], onSaveIngredient, onDeleteIngredient, onImportSuccess }) {
    const [selectedId, setSelectedId] = useState(null);
    const [form, setForm] = useState(getInitialFormState(units));
    const [compositions, setCompositions] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [ingredientImportFile, setIngredientImportFile] = useState(null);
    const [ingredientImportLoading, setIngredientImportLoading] = useState(false);
    const [ingredientImportMessage, setIngredientImportMessage] = useState('');

    const filteredIngredients = ingredients.filter((ingredient) =>
        ingredient.name.toLowerCase().includes(searchTerm.trim().toLowerCase())
    );

    useEffect(() => {
        if (selectedId === null) {
            setForm(getInitialFormState(units));
            return;
        }

        const ingredient = ingredients.find((item) => String(item.id) === String(selectedId));
        if (ingredient) {
            setForm({
                ...getInitialFormState(units),
                id: ingredient.id,
                name: ingredient.name || '',
                marque: ingredient.marque || '',
                caracteristique: ingredient.caracteristique || '',
                stock: ingredient.stock != null ? formatLocaleNumber(ingredient.stock, 0) : '',
                threshold: ingredient.threshold != null ? formatLocaleNumber(ingredient.threshold, 0) : '0',
                unit_id: ingredient.unit_id != null ? String(ingredient.unit_id) : (units[0]?.id ? String(units[0].id) : ''),
                purchasePrice: ingredient.purchasePrice != null ? formatLocaleNumber(ingredient.purchasePrice, 2) : '',
                supplier: ingredient.supplier || '',
                purchaseDate: ingredient.purchaseDate || '',
                perishable: !!ingredient.perishable,
                expirationDate: ingredient.expirationDate || '',
            });
            // Load compositions if available
            setCompositions(Array.isArray(ingredient.compositions) ? ingredient.compositions.map((c) => ({
                child_ingredient_id: c.child_ingredient_id ?? c.child_id ?? c.id ?? c.ingredient_id ?? c.ingredientId ?? c.childIngredientId,
                quantity: c.quantity ?? 0,
                unit_id: c.unit_id ?? c.unitId ?? null,
                notes: c.notes ?? null,
            })) : []);
        }
    }, [selectedId, units]);

    useEffect(() => {
        if (!form.unit_id && units.length) {
            setForm((prev) => ({ ...prev, unit_id: units[0].id ? String(units[0].id) : '' }));
        }
    }, [units]);

    const clearForm = () => {
        setSelectedId(null);
        setForm(getInitialFormState(units));
        setCompositions([]);
    };

    const getCsrfToken = () => document.querySelector('meta[name="csrf-token"]')?.content;

    const parseJsonResponse = async (response) => {
        const text = await response.text();
        try {
            return JSON.parse(text);
        } catch {
            return { message: text || 'Réponse invalide du serveur.' };
        }
    };

    const handleImportIngredients = async (event) => {
        event.preventDefault();
        if (!ingredientImportFile) {
            setIngredientImportMessage('Veuillez sélectionner un fichier CSV.');
            return;
        }

        const csrfToken = getCsrfToken();
        if (!csrfToken) {
            setIngredientImportMessage('Jeton CSRF introuvable. Rechargez la page et réessayez.');
            return;
        }

        setIngredientImportLoading(true);
        setIngredientImportMessage('');

        const formData = new FormData();
        formData.append('file', ingredientImportFile);

        try {
            const response = await fetch('/api/import/ingredients', {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    'X-CSRF-TOKEN': csrfToken,
                },
                body: formData,
            });
            const data = await parseJsonResponse(response);
            if (!response.ok) {
                setIngredientImportMessage(data.message || 'Erreur lors de l’import des ingrédients.');
            } else {
                setIngredientImportMessage(data.message || 'Import des ingrédients réussi.');
                setIngredientImportFile(null);
                const input = document.getElementById('ingredientImportFile');
                if (input) input.value = '';
                onImportSuccess?.();
            }
        } catch (error) {
            console.error('Import ingrédients error:', error);
            setIngredientImportMessage('Erreur lors de l’import des ingrédients.');
        } finally {
            setIngredientImportLoading(false);
        }
    };

    const handleSubmit = (event) => {
        event.preventDefault();
        if (!form.name.trim()) return;

        onSaveIngredient({
            ...form,
            unit_id: form.unit_id ? Number(form.unit_id) : null,
            stock: parseLocaleNumber(form.stock),
            threshold: parseLocaleNumber(form.threshold),
            purchasePrice: parseLocaleNumber(form.purchasePrice),
            compositions: compositions.map((c) => ({
                child_ingredient_id: Number(c.child_ingredient_id),
                quantity: Number(c.quantity),
                unit_id: c.unit_id ? Number(c.unit_id) : null,
                notes: c.notes || null,
            })),
        });

        clearForm();
    };

    // Composition helpers
    const addCompositionRow = () => {
        setCompositions((prev) => [...prev, { child_ingredient_id: '', quantity: 1, unit_id: '', notes: '' }]);
    };

    const updateCompositionRow = (index, field, value) => {
        setCompositions((prev) => prev.map((row, i) => (i === index ? { ...row, [field]: value } : row)));
    };

    const removeCompositionRow = (index) => {
        setCompositions((prev) => prev.filter((_, i) => i !== index));
    };

    return (
        <div className="flex flex-col h-screen space-y-6">
            <section className="rounded-3xl border border-green-400 bg-green-50/90 p-5 shadow-xl shadow-slate-950/20">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <p className="text-sm uppercase tracking-[0.35em] text-green-400">Ingrédients</p>
                        <h2 className="text-3xl font-semibold text-green-900">Gestion du stock et seuils d'alerte</h2>
                    </div>
                    <p className="max-w-xl text-sm text-green-600">
                        CRUD complet des ingrédients avec visualisation du niveau de stock et alertes de rupture.
                    </p>
                </div>
            </section>

            <div className="rounded-3xl border border-green-400 bg-white p-5 shadow-xl shadow-slate-950/10 mb-6">
                <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
                    <div>
                        <p className="text-sm uppercase tracking-[0.35em] text-green-400">Import CSV</p>
                        <h3 className="text-xl font-semibold text-green-900">Importer des ingrédients</h3>
                    </div>
                    <div className="flex flex-col gap-3 sm:flex-row">
                        <input
                            id="ingredientImportFile"
                            type="file"
                            accept=".csv"
                            onChange={(event) => setIngredientImportFile(event.target.files?.[0] ?? null)}
                            className="w-full rounded-3xl border border-green-300 bg-green-50 px-4 py-3 text-sm text-green-900 focus:border-green-500 focus:outline-none sm:w-auto"
                        />
                        <button
                            type="button"
                            onClick={handleImportIngredients}
                            disabled={ingredientImportLoading}
                            className="rounded-3xl bg-green-600 px-4 py-3 text-sm font-semibold text-white hover:bg-green-500 disabled:opacity-50"
                        >
                            {ingredientImportLoading ? 'Import en cours...' : 'Importer'}
                        </button>
                    </div>
                </div>
                {ingredientImportMessage && (
                    <p className={`mt-3 text-sm ${ingredientImportMessage.startsWith('Erreur') ? 'text-rose-600' : 'text-green-600'}`}>
                        {ingredientImportMessage}
                    </p>
                )}
                <p className="mt-3 text-sm text-slate-600">
                    Format CSV attendu : Produit, Marque, Caracteristique, Fournisseur, Prix achat, Quantité, Unité
                </p>
            </div>

            <div className="flex gap-5 flex-1 overflow-hidden">
                <div className="w-1/3 rounded-3xl border border-green-400 bg-white p-5 shadow-xl shadow-slate-950/20 overflow-y-auto">
                    <h3 className="mb-4 text-xl font-semibold text-green-900">{selectedId ? 'Modifier un ingrédient' : 'Ajouter un ingrédient'}</h3>
                    <form className="space-y-4" onSubmit={handleSubmit}>
                        <div className="grid gap-4 grid-cols-1">
                            <div>
                                <label className="text-sm text-green-600">Nom *</label>
                                <input
                                    value={form.name}
                                    onChange={(event) => setForm({ ...form, name: event.target.value })}
                                    className="mt-2 w-full rounded-3xl border border-green-400 bg-green-50 px-4 py-3 text-green-900 focus:border-green-500 focus:outline-none"
                                    placeholder="Ex. Fromage"
                                />
                            </div>
                            <div>
                                <label className="text-sm text-green-600">Marque</label>
                                <input
                                    value={form.marque}
                                    onChange={(event) => setForm({ ...form, marque: event.target.value })}
                                    className="mt-2 w-full rounded-3xl border border-green-400 bg-green-50 px-4 py-3 text-green-900 focus:border-green-500 focus:outline-none"
                                    placeholder="Ex. Ducros, Knorr…"
                                />
                            </div>
                            <div>
                                <label className="text-sm text-green-600">Caractéristique</label>
                                <input
                                    value={form.caracteristique}
                                    onChange={(event) => setForm({ ...form, caracteristique: event.target.value })}
                                    className="mt-2 w-full rounded-3xl border border-green-400 bg-green-50 px-4 py-3 text-green-900 focus:border-green-500 focus:outline-none"
                                    placeholder="Ex. Bio, frais, en vrac…"
                                />
                            </div>
                            <div>
                                <label className="text-sm text-green-600">Stock</label>
                                <input
                                    value={form.stock}
                                    onChange={(event) => setForm({ ...form, stock: event.target.value })}
                                    className="mt-2 w-full rounded-3xl border border-green-400 bg-green-50 px-4 py-3 text-green-900 focus:border-green-500 focus:outline-none"
                                    placeholder="0"
                                />
                            </div>
                            <div>
                                <label className="text-sm text-green-600">Unité</label>
                                <select
                                    value={form.unit_id ?? ''}
                                    onChange={(event) => setForm({ ...form, unit_id: event.target.value })}
                                    className="mt-2 w-full rounded-3xl border border-green-400 bg-green-50 px-4 py-3 text-green-900 focus:border-green-500 focus:outline-none"
                                >
                                    {units.map((unit) => (
                                        <option key={unit.id} value={String(unit.id)}>
                                            {unit.name}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className="text-sm text-green-600">Seuil d'alerte</label>
                                <input
                                    value={form.threshold}
                                    onChange={(event) => setForm({ ...form, threshold: event.target.value })}
                                    className="mt-2 w-full rounded-3xl border border-green-400 bg-green-50 px-4 py-3 text-green-900 focus:border-green-500 focus:outline-none"
                                    placeholder="0"
                                />
                            </div>
                            <div>
                                <label className="text-sm text-green-600">Achat (Ar)</label>
                                <input
                                    value={form.purchasePrice}
                                    onChange={(event) => setForm({ ...form, purchasePrice: event.target.value })}
                                    className="mt-2 w-full rounded-3xl border border-green-400 bg-green-50 px-4 py-3 text-green-900 focus:border-green-500 focus:outline-none"
                                    placeholder="Prix d'achat"
                                />
                            </div>
                            <div>
                                <label className="text-sm text-green-600">Fournisseur</label>
                                <input
                                    value={form.supplier}
                                    onChange={(event) => setForm({ ...form, supplier: event.target.value })}
                                    className="mt-2 w-full rounded-3xl border border-green-400 bg-green-50 px-4 py-3 text-green-900 focus:border-green-500 focus:outline-none"
                                    placeholder="Nom du fournisseur"
                                    list="supplier-options"
                                />
                                <datalist id="supplier-options">
                                    {suppliers.map((supplier) => (
                                        <option key={supplier.id} value={supplier.companyName || supplier.name || ''} />
                                    ))}
                                </datalist>
                            </div>
                            <div>
                                <label className="text-sm text-green-600">Date d'achat</label>
                                <input
                                    type="date"
                                    value={form.purchaseDate}
                                    onChange={(event) => setForm({ ...form, purchaseDate: event.target.value })}
                                    className="mt-2 w-full rounded-3xl border border-green-400 bg-green-50 px-4 py-3 text-green-900 focus:border-green-500 focus:outline-none"
                                />
                            </div>
                            <div className="flex items-center gap-3">
                                <label className="text-sm text-green-600">Périssable</label>
                                <input
                                    type="checkbox"
                                    checked={form.perishable}
                                    onChange={(event) => setForm({ ...form, perishable: event.target.checked })}
                                    className="h-5 w-5 rounded border-green-300 bg-green-50 text-green-400 focus:ring-green-400"
                                />
                            </div>
                            {form.perishable && (
                                <div>
                                    <label className="text-sm text-green-600">Date d'expiration</label>
                                    <input
                                        type="date"
                                        value={form.expirationDate}
                                        onChange={(event) => setForm({ ...form, expirationDate: event.target.value })}
                                        className="mt-2 w-full rounded-3xl border border-green-400 bg-green-50 px-4 py-3 text-green-900 focus:border-green-500 focus:outline-none"
                                    />
                                </div>
                            )}
                            <div>
                                <label className="text-sm text-green-600">Composants (sous-ingrédients)</label>
                                <div className="mt-2 space-y-2">
                                    {compositions.map((row, idx) => (
                                        <div key={idx} className="flex gap-2 items-center">
                                            <select
                                                value={String(row.child_ingredient_id || '')}
                                                onChange={(e) => updateCompositionRow(idx, 'child_ingredient_id', e.target.value)}
                                                className="w-1/2 rounded-3xl border border-green-400 bg-green-50 px-3 py-2 text-green-900"
                                            >
                                                <option value="">Sélectionner un ingrédient</option>
                                                {ingredients.filter((ing) => String(ing.id) !== String(form.id)).map((ing) => (
                                                    <option key={ing.id} value={String(ing.id)}>{ing.name}</option>
                                                ))}
                                            </select>
                                            <input
                                                type="number"
                                                min="0"
                                                step="0.01"
                                                value={row.quantity}
                                                onChange={(e) => updateCompositionRow(idx, 'quantity', e.target.value)}
                                                className="w-24 rounded-3xl border border-green-400 bg-green-50 px-3 py-2 text-green-900"
                                            />
                                            <select
                                                value={String(row.unit_id ?? '')}
                                                onChange={(e) => updateCompositionRow(idx, 'unit_id', e.target.value)}
                                                className="w-32 rounded-3xl border border-green-400 bg-green-50 px-3 py-2 text-green-900"
                                            >
                                                <option value="">Unité</option>
                                                {units.map((unit) => (
                                                    <option key={unit.id} value={String(unit.id)}>{unit.name}</option>
                                                ))}
                                            </select>
                                            <button type="button" onClick={() => removeCompositionRow(idx)} className="rounded-full bg-red-100 px-3 py-1 text-xs font-semibold text-red-700">Suppr</button>
                                        </div>
                                    ))}
                                    <div>
                                        <button type="button" onClick={addCompositionRow} className="rounded-3xl border border-green-300 bg-green-50 px-3 py-2 text-green-800">Ajouter un composant</button>
                                    </div>
                                </div>
                            </div>
                            <div className="flex gap-3">
                                <button
                                    type="submit"
                                    className="rounded-3xl bg-green-600 px-5 py-3 font-semibold text-slate-950 hover:bg-green-500"
                                >
                                    Enregistrer
                                </button>
                                <button
                                    type="button"
                                    onClick={clearForm}
                                    className="rounded-3xl border border-green-300 bg-green-50 px-5 py-3 text-green-800 hover:border-green-500"
                                >
                                    Annuler
                                </button>
                            </div>
                        </div>
                    </form>
                </div>

                <div className="w-2/3 rounded-3xl border border-green-400 bg-white p-5 shadow-xl shadow-slate-950/20 flex flex-col">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
                        <h3 className="text-xl font-semibold text-green-900">Liste des ingrédients</h3>
                        <div className="w-full sm:w-1/2">
                            <label className="text-sm text-green-600">Recherche</label>
                            <input
                                type="search"
                                value={searchTerm}
                                onChange={(event) => setSearchTerm(event.target.value)}
                                placeholder="Rechercher par nom"
                                className="mt-2 w-full rounded-3xl border border-green-400 bg-green-50 px-4 py-3 text-green-900 focus:border-green-500 focus:outline-none"
                            />
                        </div>
                    </div>
                    <div className="overflow-x-auto overflow-y-auto flex-1 mt-4">
                        <table className="min-w-full divide-y divide-green-200 text-left text-sm text-slate-700">
                            <thead className="bg-green-50 text-green-900 sticky top-0">
                                <tr>
                                    <th className="px-4 py-3 font-medium">Nom</th>
                                    <th className="px-4 py-3 font-medium">Stock</th>
                                    <th className="px-4 py-3 font-medium">Unité</th>
                                    <th className="px-4 py-3 font-medium">Seuil</th>
                                    <th className="px-4 py-3 font-medium">Dernier prix</th>
                                    <th className="px-4 py-3 font-medium">Fournisseur</th>
                                    <th className="px-4 py-3 font-medium">Expiration</th>
                                    <th className="px-4 py-3 font-medium">Actions</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-green-100 bg-white">
                                {filteredIngredients.length === 0 ? (
                                    <tr>
                                        <td colSpan="8" className="px-4 py-6 text-center text-sm text-green-600">
                                            Aucun ingrédient trouvé.
                                        </td>
                                    </tr>
                                ) : (
                                    filteredIngredients.map((ingredient) => (
                                        <tr key={ingredient.id} className="hover:bg-green-50">
                                            <td className="px-4 py-3">
                                                <div className="font-medium text-slate-900">{ingredient.name}</div>
                                                <div className="text-xs text-slate-500">{ingredient.marque || '—'}{ingredient.caracteristique ? ` • ${ingredient.caracteristique}` : ''}</div>
                                            </td>
                                            <td className="px-4 py-3">{formatLocaleNumber(ingredient.stock, 0)}</td>
                                            <td className="px-4 py-3">{units.find((unit) => String(unit.id) === String(ingredient.unit_id))?.name || '—'}</td>
                                            <td className="px-4 py-3">{formatLocaleNumber(ingredient.threshold, 0)}</td>
                                            <td className="px-4 py-3">{(() => {
                                                const lastPrice = getLastPriceForIngredient(ingredient);
                                                return lastPrice == null ? '—' : `${formatLocaleNumber(lastPrice, 2)} Ar`;
                                            })()}</td>
                                            <td className="px-4 py-3">{ingredient.supplier || '—'}</td>
                                            <td className="px-4 py-3">{ingredient.perishable ? ingredient.expirationDate || '—' : 'Non'}</td>
                                            <td className="px-4 py-3">
                                                <button
                                                    type="button"
                                                    onClick={() => setSelectedId(String(ingredient.id))}
                                                    className="mr-2 rounded-full bg-green-100 px-3 py-1 text-xs font-semibold text-green-800 hover:bg-green-200"
                                                >
                                                    Modifier
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => onDeleteIngredient(ingredient)}
                                                    className="rounded-full bg-red-100 px-3 py-1 text-xs font-semibold text-red-700 hover:bg-red-200"
                                                >
                                                    Supprimer
                                                </button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    );
}
